/** Thin typed wrappers around the workshop REST API. */

import { GroupsApi } from "@starling-cloud/sturnia-workshop";

import { apiUrl, client, request } from "./client";
import type {
  ApiFrame,
  ApiGroup,
  ApiGroupWithParticipant,
  ApiParticipant,
  FramePatch,
  GroupPatch,
} from "./types";

const V1 = "/api/v1";

export function health(): Promise<{ ok: boolean; videoEnabled: boolean }> {
  return request("GET", `${V1}/health/`);
}

/** The group / participant endpoints are the shared workshop contract
 * (@starling-cloud/sturnia-workshop/doc/contract.md); this is the typed
 * GroupsApi bound to our client and our group shape. */
export const groups = new GroupsApi<ApiGroup>(client, { basePath: V1 });

export function createGroup(
  creatorName: string,
  name?: string,
): Promise<ApiGroupWithParticipant> {
  return groups.create(creatorName, name ?? "");
}

export function getGroup(code: string): Promise<ApiGroup> {
  return groups.get(code);
}

export function patchGroup(
  code: string,
  patch: GroupPatch,
): Promise<ApiGroup> {
  return groups.patch(code, patch as Partial<ApiGroup>);
}

export function joinGroup(
  code: string,
  name: string,
): Promise<ApiGroupWithParticipant> {
  return groups.join(code, name);
}

export function qrUrl(code: string): string {
  return groups.qrUrl(code);
}

export function addParticipant(
  code: string,
  name: string,
  token: string,
): Promise<ApiParticipant> {
  return groups.addParticipant(code, name, token);
}

export function patchParticipant(
  code: string,
  participantId: string,
  name: string,
  token: string,
): Promise<ApiParticipant> {
  return groups.renameParticipant(code, participantId, name, token);
}

export function deleteParticipant(
  code: string,
  participantId: string,
  token: string,
): Promise<void> {
  return groups.removeParticipant(code, participantId, token);
}

export function patchFrame(
  code: string,
  position: number,
  patch: FramePatch,
): Promise<ApiFrame> {
  return request(
    "PATCH",
    `${V1}/groups/${encodeURIComponent(code)}/frames/${position}/`,
    { json: patch },
  );
}

export function uploadSketch(
  code: string,
  position: number,
  file: File,
): Promise<ApiFrame> {
  const form = new FormData();
  form.append("file", file);
  return request(
    "POST",
    `${V1}/groups/${encodeURIComponent(code)}/frames/${position}/sketch/`,
    { form },
  );
}

export function renderFrame(
  code: string,
  position: number,
): Promise<{ frame: ApiFrame }> {
  return request(
    "POST",
    `${V1}/groups/${encodeURIComponent(code)}/frames/${position}/render/`,
  );
}

export function renderStill(
  code: string,
  position: number,
): Promise<{ frame: ApiFrame }> {
  return request(
    "POST",
    `${V1}/groups/${encodeURIComponent(code)}/frames/${position}/still/`,
  );
}

export function renderClip(
  code: string,
  position: number,
): Promise<{ frame: ApiFrame }> {
  return request(
    "POST",
    `${V1}/groups/${encodeURIComponent(code)}/frames/${position}/clip/`,
  );
}

export function renderFilm(code: string): Promise<{ group: ApiGroup }> {
  return request(
    "POST",
    `${V1}/groups/${encodeURIComponent(code)}/film/`,
  );
}

export function filmDownloadUrl(code: string): string {
  // Followed by a plain <a>, like the QR <img> — so it must be a full
  // API URL; the backend answers with a Content-Disposition attachment.
  return apiUrl(`${V1}/groups/${encodeURIComponent(code)}/film/download/`);
}
